import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { taskApi, projectsApi, userManagementApi, teamApi } from '../../services/api';
import { Task, TaskStatus, TaskPriority } from '../../types/task';
import { ProjectListItem } from '../../types/project';
import TaskSidePanel from './components/TaskSidePanel';
import Skeleton from '../../components/Skeleton';
// @ts-ignore
import DraggableTable from '../../components/DraggableTable';
import { TASK_COLUMNS } from './components/taskColumns';
import { Plus, Search } from 'lucide-react';

const ProjectBoardPage: React.FC = () => {
  const { id } = useParams();
  const projectIdFromUrl = id ? Number(id) : null;

  const [rows, setRows] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewTaskId, setViewTaskId] = useState<number | null>(null);
  const [editTaskId, setEditTaskId] = useState<number | null>(null);
  const [commentsTaskId, setCommentsTaskId] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<number | ''>(projectIdFromUrl || '');
  const [assigneeFilter, setAssigneeFilter] = useState<number | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const loadTasks = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (projectFilter) params.project_id = projectFilter;
      if (assigneeFilter) params.assignee_id = assigneeFilter;
      if (debouncedSearchQuery) params.q = debouncedSearchQuery;

      const data = await taskApi.list(params);
      setRows(data.data || []);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => { loadTasks(); }, [statusFilter, priorityFilter, projectFilter, assigneeFilter, debouncedSearchQuery]);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const data = await projectsApi.list({});
        setProjects(data.data || []);
      } catch (error) {
        console.error('Failed to load projects:', error);
      }
    };
    loadProjects();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const data = await userManagementApi.list({ per_page: 1000 });
        setUsers(data.data || []);
      } catch (error) {
        console.error('Failed to load users:', error);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    const loadTeams = async () => {
      try {
        const data = await teamApi.list({ per_page: 1000 });
        setTeams(data.data || []);
      } catch (error) {
        console.error('Failed to load teams:', error);
      }
    };
    loadTeams();
  }, []);

  const onKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setViewTaskId(null);
      setEditTaskId(null);
      setCommentsTaskId(null);
      setShowCreateModal(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onKey]);

  const [newTask, setNewTask] = useState<{
    project_id: number | '';
    task_name: string;
    description: string;
    status: TaskStatus;
    priority: TaskPriority;
    due_date: string;
    assignee_id: number | '';
    assigned_to_team_id: number | '';
  }>({
    project_id: projectFilter || '',
    task_name: '',
    description: '',
    status: 'Not Started',
    priority: 'Normal',
    due_date: '',
    assignee_id: '',
    assigned_to_team_id: '',
  });

  const openCreateModal = () => {
    setNewTask({
      project_id: projectFilter || '',
      task_name: '',
      description: '',
      status: 'Not Started',
      priority: 'Normal',
      due_date: '',
      assignee_id: '',
      assigned_to_team_id: '',
    });
    setShowCreateModal(true);
  };

  const submitCreateTask = async () => {
    const pid = Number(newTask.project_id);
    if (!Number.isFinite(pid)) return;
    try {
      await taskApi.create({
        project_id: pid,
        task_name: newTask.task_name || 'New Task',
        description: newTask.description || '',
        status: newTask.status,
        priority: newTask.priority,
        due_date: newTask.due_date ? newTask.due_date : undefined,
        assignee_id: newTask.assignee_id ? Number(newTask.assignee_id) : undefined,
        assigned_to_team_id: newTask.assigned_to_team_id ? Number(newTask.assigned_to_team_id) : undefined,
      });
      setShowCreateModal(false);
      loadTasks();
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  const columns = TASK_COLUMNS(
    (id) => setViewTaskId(id),
    (id) => setEditTaskId(id),
    (id) => setCommentsTaskId(id),
  );

  return (
    <div className="theme-card rounded-2xl p-3 md:p-4 relative">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold theme-text-primary">Project Board</h2>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1.5 rounded-lg hover:shadow-lg transition-shadow text-sm"
        >
          <Plus className="w-3.5 h-3.5" />
          Create Task
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-3 flex-wrap">
        <select
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value ? Number(e.target.value) : '')}
          className="border border-white  theme-border rounded-lg p-2  focus:outline-none focus:border-blue-500 focus:ring-0  theme-bg-primary theme-text-primary"
        >
          <option value="">All Projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-white  theme-border rounded-lg p-2  focus:outline-none focus:border-blue-500 focus:ring-0  theme-bg-primary theme-text-primary"
        >
          <option value="">All Statuses</option>
          <option value="Not Started">Not Started</option>
          <option value="Deferred">Deferred</option>
          <option value="In Progress">In Progress</option>
          <option value="Completed">Completed</option>
          <option value="Waiting for input">Waiting for input</option>
        </select>

        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="border border-white  theme-border rounded-lg p-2  focus:outline-none focus:border-blue-500 focus:ring-0  theme-bg-primary theme-text-primary"
        >
          <option value="">All Priorities</option>
          <option value="Highest">Highest</option>
          <option value="High">High</option>
          <option value="Normal">Normal</option>
          <option value="Low">Low</option>
          <option value="Lowest">Lowest</option>
        </select>

        <select
          value={assigneeFilter}
          onChange={(e) => setAssigneeFilter(e.target.value ? Number(e.target.value) : '')}
          className="border border-white  theme-border rounded-lg p-2  focus:outline-none focus:border-blue-500 focus:ring-0  theme-bg-primary theme-text-primary"
        >
          <option value="">All Assignees</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>

        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 theme-text-tertiary w-4 h-4" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tasks..."
            className="w-full pl-10 pr-4 py-2 theme-border rounded-lg border border-white  theme-border rounded-lg p-2  focus:outline-none 
         focus:border-blue-500 
  focus:ring-0  theme-bg-primary theme-text-primary border border-white theme-bg-primary theme-text-primary"
          />
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <table className="min-w-full text-sm">
          <tbody>
            {Array.from({ length: 10 }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {Array.from({ length: 9 }).map((_, colIndex) => (
                  <td key={colIndex} className="px-2 py-3">
                    <Skeleton variant="text" width="80%" height={16} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <DraggableTable
          columns={columns}
          data={rows}
          emptyMessage="No tasks found"
        />
      )}

      {viewTaskId && (
        <TaskSidePanel
          taskId={viewTaskId}
          onClose={() => setViewTaskId(null)}
          onUpdate={loadTasks}
          mode="view"
          onModeChange={(newMode) => {
            if (newMode === 'edit') {
              setViewTaskId(null);
              setEditTaskId(viewTaskId);
            }
          }}
        />
      )}
      {editTaskId && (
        <TaskSidePanel
          taskId={editTaskId}
          onClose={() => setEditTaskId(null)}
          onUpdate={loadTasks}
          mode="edit"
        />
      )}
      {commentsTaskId && (
        <TaskSidePanel
          taskId={commentsTaskId}
          onClose={() => setCommentsTaskId(null)}
          onUpdate={loadTasks}
          mode="comments"
        />
      )}

      {showCreateModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] pt-20 pb-8" onClick={() => setShowCreateModal(false)}>
          <div
            className="theme-bg-primary rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[calc(100vh-8rem)] overflow-y-auto theme-border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 theme-bg-primary border-b theme-border px-4 py-2.5 flex items-center justify-between">
              <h3 className="text-lg font-semibold theme-text-primary">Create Task</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-2 py-1 rounded theme-bg-secondary theme-text-primary hover:opacity-80 text-sm"
              >
                Close
              </button>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="block text-sm theme-text-primary mb-0.5">Project</label>
                <select
                  className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary"
                  value={newTask.project_id}
                  onChange={(e) => setNewTask((v) => ({ ...v, project_id: e.target.value ? Number(e.target.value) : '' }))}
                >
                  <option value="">Select project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm theme-text-primary mb-0.5">Task name</label>
                <input
                  className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary"
                  value={newTask.task_name}
                  onChange={(e) => setNewTask((v) => ({ ...v, task_name: e.target.value }))}
                  placeholder="e.g., Draft easement"
                />
              </div>
              <div>
                <label className="block text-sm theme-text-primary mb-0.5">Status</label>
                <select
                  className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary"
                  value={newTask.status}
                  onChange={(e) => setNewTask((v) => ({ ...v, status: e.target.value as TaskStatus }))}
                >
                  <option>Not Started</option>
                  <option>Deferred</option>
                  <option>In Progress</option>
                  <option>Completed</option>
                  <option>Waiting for input</option>
                </select>
              </div>
              <div>
                <label className="block text-sm theme-text-primary mb-0.5">Priority</label>
                <select
                  className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary"
                  value={newTask.priority}
                  onChange={(e) => setNewTask((v) => ({ ...v, priority: e.target.value as TaskPriority }))}
                >
                  <option>Normal</option>
                  <option>Highest</option>
                  <option>High</option>
                  <option>Low</option>
                  <option>Lowest</option>
                </select>
              </div>
              <div>
                <label className="block text-sm theme-text-primary mb-0.5">Assignee</label>
                <select
                  className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary"
                  value={newTask.assignee_id}
                  onChange={(e) => setNewTask((v) => ({ ...v, assignee_id: e.target.value ? Number(e.target.value) : '' }))}
                >
                  <option value="">Unassigned</option>
                  {users.map((u: any) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm theme-text-primary mb-0.5">Team</label>
                <select
                  className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary"
                  value={newTask.assigned_to_team_id}
                  onChange={(e) => setNewTask((v) => ({ ...v, assigned_to_team_id: e.target.value ? Number(e.target.value) : '' }))}
                >
                  <option value="">No team</option>
                  {teams.map((t: any) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 px-4">
                <div className="flex justify-end gap-1.5">
                  <button onClick={() => setShowCreateModal(false)} className="px-2 py-1 rounded theme-border theme-bg-secondary theme-text-primary hover:opacity-80 text-sm">Cancel</button>
                  <button
                    onClick={submitCreateTask}
                    disabled={!newTask.project_id}
                    className="px-2 py-1 rounded bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm disabled:opacity-60 hover:shadow-lg transition-shadow"
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ProjectBoardPage;
